import { Container, Row, Col, Card, CloseButton, Modal, Badge, Button as BsButton } from "react-bootstrap";
import { useParamIds } from "./Data/Common";
import { getStateEnumFromCode, showStateName } from "./Data/UsState";
import { Link, useLoaderData, useLocation, useNavigate, useRevalidator } from "react-router-dom";
import { ParkerInfo, PaymentMethodResp, StatusOption, SubListEntry, Subscription, SubscriptionParker, SubscriptionParkerChangeReq, Vehicle } from "./Data/ApiTypes";
import { useMemo, useRef, useState } from "react";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { Hyperlink } from "./Controls/Hyperlink";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { getSpacesDisplay, getInstructions, getOrdinal, cancellationAccessPeriodEnd } from "./Data/Subscription";
import { InviteParkerModal } from "./UserPlanDetailsPageInviteParkerModal";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { CancelParkerModal } from "./Controls/CancelParkerModal";
import { ChooseVehicleModal } from "./Controls/ChooseVehicleModal";
import { CheckCircleFill, ChevronRight, Compass, CreditCard, ExclamationTriangleFill, InfoCircleFill, PencilFill, PSquareFill, QrCodeScan, SignpostSplitFill } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { showAddressHtml, showFacilityAddress } from "./Data/Location";
import { HeaderText } from "./Controls/HeaderText";
import { PageTitle } from "./Controls/PageTitle";
import { CardSection } from "./Controls/CardSection";
import { InfoBox } from "./Controls/InfoBox";
import { SubscriptionStatusPill } from "./Controls/SubscriptionStatusPill";
import QrCodeModal from "./Controls/QRCodeModal";
import { showBrowserDate, showDate } from "./Data/Date";
import React, { useEffect } from "react";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { useForm } from "react-hook-form";
import { emitSubscriptionParkerChangeReq } from "./Data/ApiEmit";
import { Instant, LocalDate, ZoneOffset } from "@js-joda/core";
import ReactivatePlanModal from "./UserPlanReactivationModal";
import UserPlanCancellationModal from "./UserPlanCancellationModal";
import ParkerEditModal from "./ParkerEditModal";
import { toast } from "react-toastify";
import { maxVehiclesOnSubscription } from "./Data/VehicleLimit";
import { WaitlistNoticeModal } from "./WaitlistNoticeModal";
import { createApiService } from "./shared/apiConfig";
import { SubscriptionApi, ServerRequestsParkerOptInFee, DataEntitiesFinanceOperatorFeeCalc, SubscriptionGroupsApi, ServerRequestsSubscriptionGroupsResponse, ServerRequestsSubscriptionGroupContractRuleDto } from "./shared/api/axios-client";
import { showMoney, cents, toCents } from "./Data/Money";
import { NoMoreSpacesModal } from "./NoMoreSpacesDialog";
import { GroupModal } from "./GroupModal";
import { useSubscriptionParkersActions } from "./UseSubscriptionParkersActions";
import {
    cancellationInstantToPickerDate,
    formatParkerCancelDate,
    isEffectiveCancellation,
} from "./Data/cancelParker";
import ContractDetailsModal, { resolvePlanBasePriceCents } from "./ContractDetailsModal";
import { CostProjectionDto } from "./CostProjectionSection";
import { CostProjectionDetailsModal } from "./CostProjectionDetailsModal";
import { costProjectionStructureKey, mapCostProjection } from "./CostProjectionMapper";
import { BillingInfoModal } from "./BillingInfoModal";
import { UserBillingAddMethodModal } from "./UserBillingAddMethodModal";
import { parseSubListEntry, parseSubscription } from "./Data/ApiParse";
import { GroupParkerListSection } from "./GroupParkerListSection";
import { ParkerVehiclesModal } from "./ParkerVehiclesModal";
import { OutstandingBalanceBanner } from "./Controls/OutstandingBalanceBanner";

enum Partner {
    ParkGuard = 0
}

export interface ReservationVehicleCardProps {
    vehicle: Vehicle;
    onRemove: ( v: Vehicle ) => void;
    subscriptionStatus: SubscriptionStatus;
}

export function ReservationVehicleCard( { vehicle, onRemove, subscriptionStatus }: ReservationVehicleCardProps ) {
    const [confirming, setConfirming] = useState( false );
    const name = [vehicle.make, vehicle.model].filter( part => part && part !== "N/A" ).join( " " );
    const label = name ? `${name} (${vehicle.licensePlateNumber})` : vehicle.licensePlateNumber;

    return <>
        <div className="mb-2 m-2">
            <div className="gap-1"
                style={{
                    display:             "grid",
                    alignItems:          "center",
                    gridTemplateColumns: "min-content 1fr auto"
                }}>
                <div className="rounded-2 border border-secondary me-2"
                    style={{ height: "32px", width: "32px", backgroundColor: vehicle.color }} />
                <div style={{ display: "grid", alignItems: "center", gridTemplateRows: "auto auto" }}>
                    <div className="fw-bold d-flex flex-column gap-1">
                        {vehicle.make} {vehicle.model}
                    </div>
                    <div className="grid-col-1">
                        {showStateName( getStateEnumFromCode( vehicle.stateCode )! )} {vehicle.licensePlateNumber}
                    </div>
                </div>
                {(subscriptionStatus == SubscriptionStatus.Active || subscriptionStatus == SubscriptionStatus.Applied) && (
                <div className="ms-3">
                    <CloseButton onClick={() => setConfirming( true )} />
                </div>
                )}
            </div>
        </div>
        <ConfirmModal
            show={confirming}
            setShow={setConfirming}
            title="Remove from subscription"
            confirmText="Remove"
            message={`Remove ${label} from this subscription?`}
            onConfirm={() => onRemove( vehicle )}
        />
    </>;
}

/** Next charge/renewal date from AP end (auto-pay). Not used for AR period labels. */
function nextRenewalDate(accessEndDate: LocalDate | null): LocalDate | null {
    if (!accessEndDate) return null;
    if (accessEndDate.isBefore(LocalDate.now())) {
        return LocalDate.now().plusMonths(1).withDayOfMonth(1);
    }
    return accessEndDate;
}

/** AR invoice period start: cost-projection upcoming window, else subscription start. */
function arInvoicePeriodStart(
    nextProjection: { periodStartDate?: string } | null,
    subscriptionStart: LocalDate | null | undefined,
): LocalDate | null {
    if (nextProjection?.periodStartDate) {
        try {
            return LocalDate.parse(nextProjection.periodStartDate);
        } catch {
            /* fall through */
        }
    }
    return subscriptionStart ?? null;
}

export function UserPlanDetailsPage() {
    const nav = useNavigate();
    const { revalidate } = useRevalidator();
    const location = useLocation();
    const fromParkersList = (location.state as { from?: string })?.from === "parkersList";
    const backTo = fromParkersList ? "/user/parkers" : "/user/plan";
    const { planId }                        = useParamIds();
    const [showInvite, setShowInviteParker] = useState( false );
    const [showAdd, setShowAdd]             = useState( false );
    const [showChoose, setShowChoose]       = useState( false );
    const [loadedSub,   loadedVehicles, loadedGroups] = useLoaderData() as [Subscription, Vehicle[],
     ServerRequestsSubscriptionGroupsResponse[]];
    const [sub,         setSub]             = useState( loadedSub );

    useEffect( () => { setSub( loadedSub ); }, [loadedSub] );

    const openPmModal = async () => {
        const [pmResult, subResult] = await Promise.all( [
            Api.stripePaymentMethodList(),
            Api.subscriptionList(),
        ] );
        if( !pmResult.isOk ) {
            toast.error( pmResult.error );
            return;
        }
        if( !subResult.isOk ) {
            toast.error( subResult.error );
            return;
        }
        setBillingData( {
            paymentMethods: pmResult.value,
            subscriptions:  subResult.value.map( parseSubListEntry ),
        } );
        setShowPmModal( true );
    };
    const [parkers,     setParkers]         = useState(() =>
        sub.parkers.filter((p) => !isEffectiveCancellation(p.removedWhen, sub.facility.timeZone))
    );
    const [subVehicles, setSubVehicles]     = useState( sub.vehicles   );
    const [myVehicles,  setMyVehicles]      = useState( loadedVehicles );
    const [showPmModal, setShowPmModal]     = useState( false );
    const [billingData, setBillingData]     = useState<{ paymentMethods: PaymentMethodResp[]; subscriptions: SubListEntry[] } | null>( null );
    const [groupPaymentMethods, setGroupPaymentMethods] = useState<PaymentMethodResp[]>([]);
    const [showReactivationModal, setShowReactivationModal] = useState(false);
    const [showCancellationModal, setShowCancellationModal] = useState(false);
    const [showLinkVehicleModal, setShowLinkVehicleModal] = useState(false);
    const [showGroupParkerVehiclesModal, setShowGroupParkerVehiclesModal] = useState(false);
    const [selectedParker, setSelectedParker] = useState<SubscriptionParker>();
    const [chooseModalVehicles, setChooseModalVehicles] = useState<Vehicle[]>([]);
    const [chooseModalExclude, setChooseModalExclude] = useState<number[]>([]);
    const [showWaitlistModal, setShowWaitlistModal] = useState(false);
    const [servicesLoading, setServicesLoading] = useState(loadedSub.status === SubscriptionStatus.Active);
    const [availableServices, setAvailableServices] = useState<ServerRequestsParkerOptInFee[]>([]);
    const [serviceSelection, setServiceSelection] = useState<{ [operatorFeeId: number]: boolean }>({});
    const [showServiceConfirm, setShowServiceConfirm] = useState(false);
    const [pendingService, setPendingService] = useState<ServerRequestsParkerOptInFee | undefined>(undefined);
    const [pendingServiceNewValue, setPendingServiceNewValue] = useState<boolean>(false);
    const [showNoSpacesModal, setShowNoSpacesModal] = useState(false);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState<ServerRequestsSubscriptionGroupsResponse | undefined>();
    const [showRemoveGroup, setShowRemoveGroup] = useState(false);
    const [groupToRemove, setGroupToRemove] =
    useState<ServerRequestsSubscriptionGroupsResponse | undefined>();
    const [groups, setGroups] =  useState<ServerRequestsSubscriptionGroupsResponse[]>(loadedGroups);
    const [selectedContract, setSelectedContract] = useState<ServerRequestsSubscriptionGroupContractRuleDto[]>([]);
    const [selectedGroupName, setSelectedGroupName] = useState<string | undefined>(undefined);
    const [showContractModal, setShowContractModal] = useState(false);

    const [currentProjection, setCurrentProjection] = useState<CostProjectionDto | null>(null);
    const [nextProjection, setNextProjection] = useState<CostProjectionDto | null>(null);
    const [costProjectionLoading, setCostProjectionLoading] = useState(false);
    const [hasFetchedCostProjection, setHasFetchedCostProjection] = useState(false);
    const [showUpcomingCostDetails, setShowUpcomingCostDetails] = useState(false);
    const [showBillingInfo, setShowBillingInfo] = useState(false);

    const [qrCodeData, setQrCodeData] = useState<string>('');
    const [showQrModal, setShowQrModal] = useState(false);
    const [qrLoading, setQrLoading] = useState(true);
    const [showPendingCancellationModifyModal, setShowPendingCancellationModifyModal] = useState(false);

    const {
    parkerRemove,
    parkerUndoCancel,
    vehicleAdd,
    vehicleRemove,
    vehicleLink,
    parkerInvite,
    openChooseModalForParkersList,
    openChooseModalForParker,
    } = useSubscriptionParkersActions({
    subscriptionId: sub.subscriptionId,
    setParkers,
    selectedParker,
    setSelectedParker,
    onNoSpaces: () => setShowNoSpacesModal(true),
      setShowChooseVehicle: setShowChoose,
      setShowAddVehicle: setShowAdd,
      setChooseModalExclude: setChooseModalExclude,
      setChooseModalVehicles: setChooseModalVehicles,
      setSubVehicles,
      ownerParkerId: sub.parkerId,
      onAfterParkerChange: () => {
        void Api.subscriptionDetails(planId).then((result) => {
          if (!result.isOk) return;
          setSub(parseSubscription(result.value));
        });
      },
    });


    const subscription = useLoaderData();

    const subGroupsApi = createApiService(SubscriptionGroupsApi);
    const isEnhancedGroup = sub.isEnhancedGroup === true;
    const isGroup         = sub.isGroup === true && !isEnhancedGroup;
    /** Plain IsGroup or enhanced — both use parker cards / calculated group billing UI. */
    const isCalculatedGroup = isGroup || isEnhancedGroup;
    const groupParkerSectionRef = useRef<HTMLDivElement>(null);

    const { getValues } = useForm<SubscriptionParkerChangeReq>( {
        defaultValues: { ...emitSubscriptionParkerChangeReq(subscription) }
    } );

    const changeSubscriptionStatus = async (selectedStatus: SubscriptionStatus, cancellationDate?: string) => {
        const res = await Api.changeSubscriptionStatus(planId, selectedStatus, undefined, cancellationDate);

        processResult(
            res,
            async (responseData) => {
                setSub(prev => {
                    if (selectedStatus === SubscriptionStatus.Active && prev.end != null) {
                        return { ...prev, status: SubscriptionStatus.Active, end: null, staffInitiatedCancellation: false };
                    }
                    if (selectedStatus !== SubscriptionStatus.Cancelled) {
                        return {
                            ...prev,
                            status: selectedStatus,
                            ...(selectedStatus === SubscriptionStatus.Active
                                ? { staffInitiatedCancellation: false }
                                : {}),
                        };
                    }
                    const accessEnd = prev.accessPeriods.length
                        ? prev.accessPeriods.reduce((latest, ap) => ap.end.isAfter(latest.end) ? ap : latest, prev.accessPeriods[0]).end
                        : prev.end;
                    return {
                        ...prev,
                        status: SubscriptionStatus.Active,
                        end: cancellationDate ? LocalDate.parse(cancellationDate) : accessEnd,
                        staffInitiatedCancellation: false,
                    };
                });
                setShowCancellationModal(false);
                setShowReactivationModal(false);
                
                // If reactivating and the API returned fee data, update the services
                if (selectedStatus === SubscriptionStatus.Active && responseData) {
                    try {
                        // Check if responseData contains fee information
                        const data = typeof responseData === 'string' ? JSON.parse(responseData) : responseData;
                        if (data && (data.parkerOptInFees || data.availableOptInFees)) {
                            const fees = data.parkerOptInFees ?? data.availableOptInFees ?? [];
                            setAvailableServices(fees);
                            const defaults: { [id: number]: boolean } = {};
                            fees.forEach(fee => {
                                const id = fee.operatorFeeId || 0;
                                const isOptedIn = (fee as any)?.isOptedIn as boolean | undefined;
                                defaults[id] = isOptedIn ?? (fee.isDefaultOptIn || false);
                            });
                            setServiceSelection(defaults);
                        }
                    } catch (parseError) {
                        // If parsing fails, reload services from API
                        await reloadServices();
                    }
                }
            },
            err => {
                toast.error(err);
            }
        );
    };

    const openChooseModalForSubList = () => {
        setChooseModalVehicles(myVehicles);
        setChooseModalExclude(subVehicles.map(sv => sv.vehicleId));
        setShowChoose(true);
    };

    //remove parker
    const [showRemove,   setShowRemove]     = useState( false );
    const [removeParkerEntry, setRemoveParkerEntry] = useState<SubscriptionParker>();
    const [showRemoveSlot, setShowRemoveSlot] = useState(false);

    //convenience vars
    const plan                              = sub.plan;
    const facility                          = sub.facility;
    const quantity                          = sub.quantity;
    const isUsageBasedBilling               = plan.isUsageBasedBilling;
    const isComplimentaryPlan               = toCents(sub.price) === 0;
    const planIsActive                      = sub.status === SubscriptionStatus.Active;
    const vehicleLimit                      = maxVehiclesOnSubscription( false, false, quantity );
    const linkedVehicleCount                = subVehicles.length + parkers.reduce( ( n, p ) => n + ( p.vehicles?.length ?? 0 ), 0 );
    const canAddVehicle                     = planIsActive && linkedVehicleCount < vehicleLimit;
    const planIsCancelled                   = sub.status === SubscriptionStatus.Cancelled;

    const cancel = {
        lbl:  "Plan Is Cancelled",
        expl: "The Plan will be cancelled and will not renew",
        val: SubscriptionStatus.Active // set the plan as active, if it's cancelled
    };
    
    const reactivate = {
        lbl:  "Plan Is Active",
        expl: "Set to Active",
        val: SubscriptionStatus.Cancelled // set the plan as cancelled, if it's active
    };

    const filterStatusOptions = ( status: SubscriptionStatus ): StatusOption[] => {
        if( status === SubscriptionStatus.Cancelled ) {
            return [cancel];
        }
        if( status === SubscriptionStatus.Active ) {
            return [reactivate];
        }
        return [];
    }

    const filteredOptions: StatusOption[] = [
        ...filterStatusOptions( sub.status )
    ];

    const handleUndoPendingCancellation = async () => {
        await changeSubscriptionStatus(SubscriptionStatus.Active);
    };

    const handlePlanReactivate = async () => {
        if (staffCancellationLocked) return;
        if (sub.status === SubscriptionStatus.Active && sub.end != null) {
            await handleUndoPendingCancellation();
            return;
        }
        if (facility.isWaitlistEnabled) {
            setShowWaitlistModal(true);
        }
        else if (furthestAccessPeriodDate!.isAfter(LocalDate.now())) {
            await changeSubscriptionStatus(SubscriptionStatus.Active);
            // TODO: add audit entry
        }
        else {
             setShowReactivationModal(true);
        }
    }

    const handleActivateNow = () => {
        setShowReactivationModal(false);
        
        nav(`/user/plan/${planId}/edit`)
        // TODO: add audit entry
    }    

    const handleActivateNextCycle = async () => {
        await changeSubscriptionStatus(SubscriptionStatus.Active);
        setShowReactivationModal(false);
        // TODO: add audit entry
    }

    const handleModifyPlanClick = () => {
        if (sub.status === SubscriptionStatus.Active && sub.end != null) {
            setShowPendingCancellationModifyModal(true);
            return;
        }
        if (sub.isEnhancedGroup) {
            nav(`/user/plan/${planId}/groups/parkers`);
        } else {
            nav(`/user/plan/${planId}/edit`);
        }
    };

    const openEditGroup = (group: ServerRequestsSubscriptionGroupsResponse) => {
    setEditingGroup(group);
    setShowGroupModal(true);
    };

    // Function to reload services from API
    const reloadServices = async () => {
        try {
            setServicesLoading(true);
            const api = createApiService(SubscriptionApi);
            const response = await api.getParkerOptedInFees({
                serverRequestsGetParkerOptInFeesRequest: {
                    parkerId: loadedSub.parkerId,
                    subscriptionId: loadedSub.subscriptionId,
                    facilityId: facility.facilityId,
                }
            });
            const data: any = response.data as any;
            const fees = data.parkerOptInFees ?? data.availableOptInFees ?? [];
            setAvailableServices(fees);
            const defaults: { [id: number]: boolean } = {};
            fees.forEach(fee => {
                const id = fee.operatorFeeId || 0;
                const isOptedIn = (fee as any)?.isOptedIn as boolean | undefined;
                defaults[id] = isOptedIn ?? (fee.isDefaultOptIn || false);
            });
            setServiceSelection(defaults);
        } catch (err) {
            setAvailableServices([]);
            setServiceSelection({});
        } finally {
            setServicesLoading(false);
        }
    };

    useEffect(() => {
        if (loadedSub.status !== SubscriptionStatus.Active) {
            setServicesLoading(false);
            return;
        }
        reloadServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedSub.subscriptionId, loadedSub.parkerId, facility.facilityId]);

    const handleServiceToggle = (operatorFeeId: number, checked: boolean) => {
        const svc = availableServices.find(f => (f.operatorFeeId || 0) === operatorFeeId);
        
        // Check if trying to activate an inactive ParkGuard service
        if (checked && (svc as any)?.partner === Partner.ParkGuard && !serviceSelection[operatorFeeId]) {
            toast.error("This service cannot be activated");
            return;
        }
        
        setPendingService(svc);
        setPendingServiceNewValue(checked);
        setShowServiceConfirm(true);
    };

    
    useEffect(() => {
        if (!planIsActive) {
            setQrLoading(false);
            return;
        }
        setQrLoading(true);
        const subscriptionApi = createApiService(SubscriptionApi);
        subscriptionApi.getParkerAccessCode({
            parkerId: sub.parkerId,
            subscriptionId: sub.subscriptionId,
        })
        .then((accessCode) => {
            const response = accessCode.data as any;
            if (response?.metaFlashRFIDNumber) {
                setQrCodeData(response.metaFlashRFIDNumber);
            }
        })
        .catch(() => {})
        .finally(() => setQrLoading(false));
    }, []);

    // Load payment methods for IsGroup / enhanced subscriptions (prorate add-parker flow)
    useEffect(() => {
        if (!isCalculatedGroup || !planIsActive) return;
        Api.stripePaymentMethodList().then(result => {
            if (result.isOk) setGroupPaymentMethods(result.value);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCalculatedGroup, sub.subscriptionId]);

    const confirmServiceChange = async () => {
        if (!pendingService) {
            setShowServiceConfirm(false);
            return;
        }
        const id = pendingService.operatorFeeId || 0;

       
        // Call the API to persist the change
        try {
            const api = createApiService(SubscriptionApi);
            const response = await api.changeOptInFee({
                serverRequestsChangeOptInFeeRequest: {
                    parkerId: loadedSub.parkerId,
                    subscriptionId: loadedSub.subscriptionId,
                    operatorFeeId: id,
                    isOptIn: pendingServiceNewValue,
                }
            });

            // Update service selection
            setServiceSelection(prev => ({ ...prev, [id]: pendingServiceNewValue }));
            
            // Update the fee data in availableServices with the response data
            if (response.data) {
                const responseData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
                // The API returns a single ServerRequestsParkerOptInFee object, not an array
                if (responseData && responseData.operatorFeeId) {
                    setAvailableServices(prevServices => {
                        return prevServices.map(fee => {
                            // Update the specific fee that was changed
                            if (fee.operatorFeeId === responseData.operatorFeeId) {
                                return { ...fee, ...responseData };
                            }
                            return fee;
                        });
                    });
                }
            }
            
            setShowServiceConfirm(false);
            toast.success("Updated service enrollment successfully");
        } catch (e) {
            // Revert the optimistic update on error
            setServiceSelection(prev => ({ ...prev, [id]: !pendingServiceNewValue }));
            console.error("Failed to update opt-in fee status:", e);
            toast.error("Failed to update service enrollment");
        }
    };

    const cancelServiceChange = () => {
        setShowServiceConfirm(false);
        setPendingService(undefined);
    };

    const formatServiceAmount = (fee: ServerRequestsParkerOptInFee) => {
        if ((fee as any)?.operatorFeeCalc === DataEntitiesFinanceOperatorFeeCalc.NUMBER_1) {
            return `${fee.amount}%`;
        }
        return showMoney(cents(fee.amount || 0));
    };

    async function createGroup(
    name: string
    ) {
    const res = await subGroupsApi.addGroup({
        serverRequestsSubscriptionGroupAddRequest: {
        subscriptionId: sub.subscriptionId,
        name,
        },
    });

    const group = res.data;

    if (group?.error) {
        toast.error(group.error);
        return;
    }

    setGroups(prev => [
        ...prev,
        {
        id: group.id,
        name: group.name,
        parkerCount: 0,
        },
    ]);

    toast.success('Group created successfully.');

    return group;
    }

    async function updateGroup(
    groupId: number,
    name: string
    ) {
    const res = await subGroupsApi.updateGroup({
        subscriptionGroupId: groupId,
        name,
        },
    );

    const group = res.data;

    if (group?.error) {
        toast.error(group.error);
        return;
    }

    setGroups(g =>
        g.map(gr =>
            gr.id === groupId ? { ...gr, name } : gr
        )
    );

    toast.success('Group updated successfully.');

    return group;
    }

    async function deleteGroup(groupId: number) {
    const res = await subGroupsApi.deleteGroup({
        subscriptionGroupId: groupId,
        },
    );

    if (res.data?.error) {
        toast.error(res.data?.error);
        return;
    }

    setGroups(g =>
        g.filter(gr => gr.id !== groupId)
    );

    toast.success('Group removed successfully.');
    }

    const furthestAccessPeriodDate: LocalDate | null = sub.accessPeriods.reduce((latest, ap) => ap.end.isAfter(latest.end) ? ap 
    : latest, sub.accessPeriods[0])?.end;
    const pendingCancellation = sub.status === SubscriptionStatus.Active && sub.end != null;
    const isPending = [
        SubscriptionStatus.Applied,
        SubscriptionStatus.WaitingForContract,
        SubscriptionStatus.Invited,
        SubscriptionStatus.Waitlisted,
    ].includes( sub.status );
    const staffCancellationLocked = sub.staffInitiatedCancellation;
    const nextRenewal = nextRenewalDate(furthestAccessPeriodDate);
    const pendingAccessEndDate = sub.end ? cancellationAccessPeriodEnd(sub.end) : null;
    const showAutoRenewSubtext = !(
        pendingCancellation && sub.end != null && nextRenewal != null && nextRenewal.isAfter(sub.end)
    );

    const instr      = isComplimentaryPlan
        ? ''
        : getInstructions(sub.status, furthestAccessPeriodDate, (sub.paymentMethod !== null && sub.paymentMethod.length > 0), sub.end);
    const spacesDisplay = getSpacesDisplay( sub );
    const spaces        = spacesDisplay.count;

    const totalSpaces = sub.quantity;
    const totalParkers = parkers.length;

    const unallocatedSpaces = totalSpaces - totalParkers;

    const unassignedCount = useMemo(
        () => parkers.filter((p) => !p.subscriptionGroupId).length,
        [parkers]
    );

    const hasUnassigned = unassignedCount > 0;
    const hasUnallocated = unallocatedSpaces > 0;

    const showWarning = hasUnassigned || hasUnallocated;

    const refreshCostProjection = async () => {
    setCostProjectionLoading(true);
    setHasFetchedCostProjection(true);
    try {
        const subscriptionApi = createApiService(SubscriptionApi);
        const response = (await subscriptionApi.subscriptionIdCostProjectionGet({subscriptionId: sub.subscriptionId})).data!;

        setCurrentProjection(
            response.currentPeriod
                ? mapCostProjection(response.currentPeriod, parkers)
                : null,
        );
        setNextProjection(
            response.nextPeriod
                ? mapCostProjection(response.nextPeriod, parkers)
                : null,
        );
    } finally {
        setCostProjectionLoading(false);
    }
    };

    // Scroll to top when the page mounts (e.g. after navigating from plans list)
    useEffect(() => {
        window.scrollTo(0, 0);
        document.getElementById('content')?.scrollTo(0, 0);
    }, []);

    // IsGroup / enhanced: eager-load estimated upcoming costs on mount
    useEffect(() => {
        if (!isCalculatedGroup) return;
        refreshCostProjection();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only for group types
    }, [isCalculatedGroup, sub.subscriptionId]);

    const costProjectionKey = useMemo(
        () => costProjectionStructureKey(parkers, sub.quantity),
        [parkers, sub.quantity],
    );

    // Auto-refresh after plan/parker/quantity changes once projection has been fetched
    // (name/email/phone edits do not change costProjectionKey)
    useEffect(() => {
        if (!isCalculatedGroup) return;
        if (hasFetchedCostProjection) {
            refreshCostProjection();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, costProjectionKey]);



    return <Container fluid>
        <InviteParkerModal show={showInvite}
            setShow={setShowInviteParker}
            submit={parkerInvite}
            isEnhancedGroup={isEnhancedGroup}
            isGroup={sub.isGroup}
            subscriptionStart={sub.start}/>

        {(isCalculatedGroup) && currentProjection && nextProjection && (
            <CostProjectionDetailsModal
                show={showUpcomingCostDetails}
                onHide={() => setShowUpcomingCostDetails(false)}
                title="Cost Breakdown"
                sections={[
                    ...((currentProjection.total.amount ?? 0) > 0
                        || currentProjection.groups.some((g) => g.parkerCount > 0)
                        ? [{ title: "Current period activity", data: currentProjection, variant: "current" as const }]
                        : []),
                    { title: "Upcoming period", data: nextProjection, variant: "upcoming" as const },
                ]}
            />
        )}

        <BillingInfoModal
            show={showBillingInfo}
            onHide={() => setShowBillingInfo(false)}
            isAccountsReceivable={!!sub.isAccountsReceivable}
        />

        <AddVehicleModal
            show={showAdd}
            setShow={setShowAdd}
            onSubmit={vehicleAdd}
            backdrop={showGroupParkerVehiclesModal || showLinkVehicleModal ? false : true}
        />

        <ChooseVehicleModal
        show={showChoose}
        setShow={setShowChoose}
        onSubmit={vehicleLink}
        vehicles={chooseModalVehicles}
        exclude={chooseModalExclude}
        onClose={() => setShowChoose(false)}
        backdrop={showGroupParkerVehiclesModal || showLinkVehicleModal ? false : true}
        />

        <CancelParkerModal
            show={showRemove}
            onHide={() => setShowRemove(false)}
            defaultCancellationDate={
                removeParkerEntry?.removedWhen
                    ? cancellationInstantToPickerDate(removeParkerEntry.removedWhen, facility.timeZone)
                    : furthestAccessPeriodDate
                        ? furthestAccessPeriodDate.minusDays(1).toString()
                        : undefined
            }
            onConfirm={(cancellationDate) => {
                if (removeParkerEntry !== undefined) {
                    parkerRemove(removeParkerEntry.parker.parkerId, undefined, cancellationDate);
                }
                setShowRemove(false);
            }}
        />

        <ConfirmModal
            show={showRemoveSlot}
            setShow={setShowRemoveSlot}
            title="Remove Open Spot"
            message="Remove this open spot from your plan? Quantity decreases on the next billing period. The current period is not affected."
            confirmText="Remove Spot"
            onConfirm={() => {
                if (sub.quantity <= 1) {
                    toast.error("Plan must keep at least 1 spot.");
                    return;
                }
                Api.subscriptionUpdateNextCycle({
                    subscriptionId: sub.subscriptionId,
                    newQuantity: sub.quantity - 1,
                }).then(res => processResult(res,
                    () => {
                        setSub(prev => ({ ...prev, quantity: prev.quantity - 1 }));
                        toast.success("Open spot removed for next billing period.");
                    },
                    err => toast.error(err)
                ));
            }}
        />

        <ReactivatePlanModal
            show={showReactivationModal}
            onHide={() => setShowReactivationModal(false)}
            onActivateNow={handleActivateNow}
            onActivateNextCycle={handleActivateNextCycle}
        />
        <UserPlanCancellationModal
            show={showCancellationModal}
            onHide={() => setShowCancellationModal(false)}
            onConfirmCancel={async (cancellationDate: string) => await changeSubscriptionStatus(SubscriptionStatus.Cancelled, cancellationDate)}
            cancellationWindowDays={plan.cancellationWindowDays ?? 0}
            defaultCancellationDate={furthestAccessPeriodDate ? furthestAccessPeriodDate.minusDays(1).toString() : undefined}
            nextRenewalDate={nextRenewal?.toString()}
            hasWaitlistEntries={facility.hasWaitlistEntries}
            isWaitlistEnabled={facility.isWaitlistEnabled}
            currentSpaces={sub.quantity}
        />
        {showGroupParkerVehiclesModal && selectedParker && (
            <ParkerVehiclesModal
                show={showGroupParkerVehiclesModal}
                onClose={() => {
                    setShowGroupParkerVehiclesModal(false);
                    setSelectedParker(undefined);
                }}
                parker={selectedParker}
                subStatus={sub.status}
                onAddNew={() => setShowAdd(true)}
                onAddExisting={() => openChooseModalForParker(selectedParker)}
                onRemoveVehicle={vehicleRemove}
            />
        )}
        {showLinkVehicleModal && selectedParker && (
            <ParkerEditModal
                show={showLinkVehicleModal}
                onClose={() => {
                setShowLinkVehicleModal(false);
                setSelectedParker(undefined);
                }}
                parker={selectedParker}
                setParker={setSelectedParker}
                subStatus={sub.status}
                onAddNew={() => setShowAdd(true)}
                onAddExisting={() => openChooseModalForParkersList()}
                onRemoveVehicle={vehicleRemove}
                subId={loadedSub.subscriptionId}
                parkers={parkers}
                setParkers={setParkers}
                initialWaitlistRequestPayload={{
                parkerId: selectedParker.parker.parkerId,
                facilityId: facility.facilityId,
                planEntryId: sub.planEntryId,
                spacesRequested: selectedParker.quantity, 
                isAddingToExistingParker: true,
                subscriptionId: sub.subscriptionId
            }}
                isWaitlistEnabled= {facility.isWaitlistEnabled}
                hasWaitlistEntries={facility.hasWaitlistEntries}
                currentSpaces={selectedParker.quantity}
                planVehicleCount={linkedVehicleCount}
                planVehicleLimit={vehicleLimit}
            />
        )} 

        {billingData && (
        <UserBillingAddMethodModal
            show={showPmModal}
            onHide={() => {
                setShowPmModal( false );
                setBillingData( null );
            }}
            subscriptions={billingData.subscriptions}
            paymentMethods={billingData.paymentMethods}
            onSuccess={() => revalidate()}
            mode="modify"
            initialSubscriptionIds={[sub.subscriptionId]}
            contextPlan={{ name: plan.name, facility: facility.name }}
            cardOnly={isUsageBasedBilling}
            skipPlanSelect={isUsageBasedBilling}
        />
        )}

        <Row>
            <Col>
                <Container fluid className="g-0">
                    <Row>
                        <Col xs="12">
                            <div className="d-flex align-items-center gap-3">
                                <BsButton variant="link" className="p-0" onClick={() => nav(backTo)}>← Back</BsButton>
                                <PageTitle>
                                    {facility.name}
                                </PageTitle>
                            </div>
                            <div className="d-flex gap-2 fw-semibold text-muted align-items-center">
                                <SubscriptionStatusPill accent={true} className="small fw-normal" status={sub.status} startDate={sub.start} />{plan.name}
                            </div>
                            <OutstandingBalanceBanner className="mt-2 mb-0" />
                            {pendingCancellation && (
                                <div
                                    className="alert alert-muted-gold d-flex align-items-start gap-2 mt-2 mb-0 small"
                                >
                                    <ExclamationTriangleFill size={14} className="flex-shrink-0 mt-1" />
                                    <div>
                                        {pendingAccessEndDate && (
                                            <div className="fw-semibold">
                                                Access ends {showDate(pendingAccessEndDate)}.
                                            </div>
                                        )}
                                        <div className={pendingAccessEndDate ? "mt-1" : undefined}>
                                            Pending cancellation on {showDate(sub.end!)}.
                                            {!staffCancellationLocked && (
                                                <>
                                                    {" "}
                                                    <BsButton
                                                        variant="link"
                                                        className="p-0 text-decoration-underline align-baseline border-0"
                                                        style={{ lineHeight: "inherit", color: "#6f4500", fontSize: "inherit", fontWeight: "inherit" }}
                                                        onClick={handleUndoPendingCancellation}
                                                    >
                                                        Undo cancellation
                                                    </BsButton>
                                                </>
                                            )}
                                        </div>
                                        {staffCancellationLocked && (
                                            <div className="mt-1">
                                                This cancellation was initiated by the facility. Contact them to make changes.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {staffCancellationLocked && planIsCancelled && (
                                <div className="small mt-2 text-muted">
                                    This cancellation was initiated by the facility. Contact them to reactivate your plan.
                                </div>
                            )}
                            {isUsageBasedBilling && (
                                <InfoBox className="mt-3">
                                    <div className="small">
                                        With usage-based billing, <strong>{facility.name}</strong> charges your saved card for parking activity in their lot.
                                        Charges apply automatically based on your actual usage and the rates posted at the facility — there is no monthly subscription fee.
                                        {' '}View past charges on the{' '}
                                        <Link to="/user/order">Orders</Link> page.
                                    </div>
                                </InfoBox>
                            )}
                            {isComplimentaryPlan && (planIsActive || planIsCancelled)
                                ? null
                                : (planIsActive || planIsCancelled) && !pendingCancellation ? (
                            <div className="my-3 rounded-3 border overflow-hidden">
                                <div className="d-flex align-items-center justify-content-between px-3 py-2"
                                     style={{ backgroundColor: planIsActive
                                        ? 'color-mix(in srgb, var(--bs-success) 6%, white)'
                                        : 'color-mix(in srgb, var(--bs-danger) 6%, white)' }}>
                                    <div>
                                        <div className="fw-bold">{planIsActive ? 'Plan active' : 'Plan inactive'}</div>
                                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                            {sub.isAccountsReceivable
                                                ? `Invoiced on the ${getOrdinal(plan.dayOfMonthInvoice)} of each month`
                                                : planIsActive 
                                                    ? (showAutoRenewSubtext
                                                        ? (nextRenewal
                                                            ? `Auto-renews on ${showDate(nextRenewal)}`
                                                            : 'Auto-renews')
                                                        : pendingCancellation && pendingAccessEndDate
                                                            ? `Access ends ${showDate(pendingAccessEndDate)}`
                                                            : '')
                                                    : (furthestAccessPeriodDate
                                                        ? (furthestAccessPeriodDate.isAfter(LocalDate.now())
                                                            ? `Access ends ${showDate(furthestAccessPeriodDate)}`
                                                            : `Access ended ${showDate(furthestAccessPeriodDate)}`)
                                                        : 'Access ended')}
                                        </div>
                                    </div>
                                    <div className="form-check form-switch m-0 p-0 d-flex align-items-center" style={{ minHeight: 0 }}>
                                        <input
                                            className="form-check-input m-0 fs-4"
                                            type="checkbox"
                                            role="switch"
                                            id="plan-status-toggle"
                                            checked={planIsActive}
                                            disabled={staffCancellationLocked && (pendingCancellation || planIsCancelled)}
                                            onChange={() => {
                                                if (staffCancellationLocked) return;
                                                if (planIsCancelled) {
                                                    handlePlanReactivate();
                                                } else {
                                                    setShowCancellationModal(true);
                                                }
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                backgroundColor: planIsActive ? 'var(--color-mid)' : '',
                                                borderColor: planIsActive ? 'var(--color-mid)' : '',
                                            }}
                                        />
                                    </div>
                                </div>
                                {instr ? (
                                <div className={`d-flex align-items-start gap-2 px-3 py-2 ${planIsActive ? 'bg-success-subtle' : 'bg-danger-subtle'}`}>
                                    {planIsActive
                                        ? <CheckCircleFill className="flex-shrink-0 text-success mt-1" />
                                        : <ExclamationTriangleFill className="flex-shrink-0 text-warning mt-1" />}
                                    <span className="small">{instr}</span>
                                </div>
                                ) : null}
                            </div>
                            ) : (
                                instr && !pendingCancellation ? <InfoBox variant="gold">{instr}</InfoBox> : null
                            )}
                            {/* {showWarning && isEnhancedGroup && (
                                <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3">
                                <InfoCircleFill color="warning" className="mt-1" />
                        
                                <div>                                
                                    <div className="fw-semibold mb-1">Attention Required</div>
                        
                                    <div className="small mb-2">
                                    The following will be charged the base rate of{" "}
                                    <strong>{showMoney(sub.price)}</strong>:
                                    </div>
                        
                                    <ul className="mb-0 ps-3 small">
                                    {hasUnassigned && (
                                        <li>
                                        <strong>{unassignedCount}</strong> parker
                                        {unassignedCount > 1 ? "s are" : " is"} not assigned to a
                                        group
                                        </li>
                                    )}
                        
                                    {hasUnallocated && (
                                        <li>
                                        <strong>{unallocatedSpaces}</strong> unallocated space
                                        {unallocatedSpaces > 1 ? "s" : ""}
                                        </li>
                                    )}
                                    </ul>
                                    {planIsActive && groups.length > 0 && (
                                        <div className="mt-2">  
                                            <Link to={`/user/plan/${planId}/groups/parkers`} >
                                                Manage Plan
                                            </Link>
                                        </div>
                                    )}
                                </div>
                                </div>
                            )} */}

                        </Col>
                    </Row>
                    <Row>
                        <Col xs="12">
                            <Card className="mt-2 mt-1 rounded-3 shadow-subtle overflow-hidden">
                                <div className="d-flex align-items-center gap-3 px-3 py-3 border-bottom">
                                    <div className="rounded-3 flex-shrink-0 d-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40, backgroundColor: 'var(--color-mid)' }}>
                                        <Compass />
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="text-muted small text-uppercase">Location</div>
                                        <div className="fw-semibold">{facility.address.addressLine1}</div>
                                        <div className="text-muted small">{facility.address.city}, {facility.address.state} {facility.address.zipCode}</div>
                                    </div>
                                    <BsButton variant="outline-secondary" size="sm" className="rounded-pill d-flex align-items-center gap-1 flex-shrink-0"
                                        onClick={() => window.open( `http://maps.apple.com/?q=${encodeURIComponent( showFacilityAddress( facility.address ) )}` )}>
                                        <SignpostSplitFill className="small" /> Directions
                                    </BsButton>
                                </div>

                                {planIsActive && !isCalculatedGroup && !sub.plan.isOneTermParking && !isUsageBasedBilling && (
                                <div className="d-flex align-items-center gap-3 px-3 py-3 border-bottom">
                                    <div className="rounded-3 flex-shrink-0 d-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40, backgroundColor: 'var(--color-mid)' }}>
                                        <PSquareFill />
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="text-muted small text-uppercase">Parking</div>
                                        {!isComplimentaryPlan ? (
                                        <>
                                        <div className="fw-semibold">{spaces} {spaces === 1 ? 'Spot' : 'Spots'}</div>
                                        <div className="text-muted small">
                                            {spacesDisplay.kind === 'upcoming'
                                                ? `Starts on ${showDate(spacesDisplay.startDate)}`
                                                : showAutoRenewSubtext
                                                    ? `Renews with ${sub.quantity} ${sub.quantity === 1 ? 'spot' : 'spots'}`
                                                    : ''}
                                        </div>
                                        </>
                                        ) : (
                                        <>
                                        <div className="fw-semibold">This is a complimentary plan</div>
                                        {sub.start.isAfter(LocalDate.now()) && (
                                        <div className="text-muted small">
                                            {sub.quantity} {sub.quantity === 1 ? 'Spot' : 'Spots'} · Starts on {showDate(sub.start)}
                                        </div>
                                        )}
                                        </>
                                        )}
                                    </div>
                                    {!isComplimentaryPlan && (
                                    <BsButton variant="outline-secondary" size="sm"
                                        className={`rounded-pill d-flex align-items-center gap-1 flex-shrink-0${pendingCancellation ? ' opacity-50' : ''}`}
                                        aria-disabled={pendingCancellation}
                                        onClick={handleModifyPlanClick}>
                                        <PencilFill className="small" /> Modify
                                    </BsButton>
                                    )}
                                </div>
                                )}

                                {planIsActive && !sub.end && (isUsageBasedBilling || !isComplimentaryPlan) && (
                                <div className={`d-flex align-items-center gap-3 px-3 py-3${sub.isPendingPaymentMethodVerification ? " rounded-bottom-3" : ""}`}
                                     style={sub.isPendingPaymentMethodVerification ? { backgroundColor: "#fff8f0" } : undefined}>
                                    <div className="rounded-3 flex-shrink-0 d-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40, backgroundColor: sub.isPendingPaymentMethodVerification ? "#fd7e14" : 'var(--color-mid)' }}>
                                        {sub.isPendingPaymentMethodVerification ? <ExclamationTriangleFill size={18} /> : <CreditCard />}
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="text-muted small text-uppercase">
                                            Billing
                                        </div>
                                        {sub.isPendingPaymentMethodVerification ? (
                                            <>
                                                <div className="fw-semibold" style={{ color: "#e8650a" }}>Bank account verification pending</div>
                                                <div className="small" style={{ color: "#a04500" }}>
                                                    Check your email for a link to verify your bank account
                                                </div>
                                            </>
                                        ) : (isCalculatedGroup) ? (
                                            <>
                                                {costProjectionLoading || !currentProjection || !nextProjection ? (
                                                    <div className="placeholder-glow">
                                                        <span className="placeholder col-7 rounded d-inline-block" style={{ height: 20 }} />
                                                        <div className="mt-1">
                                                            <span className="placeholder col-5 rounded d-inline-block" style={{ height: 12 }} />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="d-flex flex-column" style={{ gap: 1 }}>
                                                            <div className="fw-semibold">
                                                                {(() => {
                                                                    const upcomingTotal =
                                                                        (currentProjection.total.amount ?? 0) +
                                                                        (nextProjection.total.amount ?? 0);
                                                                    if (sub.isAccountsReceivable) {
                                                                        const periodStart = arInvoicePeriodStart(
                                                                            nextProjection,
                                                                            sub.start,
                                                                        );
                                                                        return periodStart
                                                                            ? `${showMoney(cents(upcomingTotal))} for period starting ${showDate(periodStart)}`
                                                                            : showMoney(cents(upcomingTotal));
                                                                    }
                                                                    const renewal = showDate(
                                                                        nextRenewalDate(furthestAccessPeriodDate) ?? furthestAccessPeriodDate,
                                                                    );
                                                                    return renewal
                                                                        ? `${showMoney(cents(upcomingTotal))} on ${renewal}`
                                                                        : showMoney(cents(upcomingTotal));
                                                                })()}
                                                            </div>
                                                            <div
                                                                className="d-flex flex-column flex-sm-row align-items-start gap-1"
                                                                style={{ fontSize: "0.75rem" }}
                                                            >
                                                                {((currentProjection.total.amount ?? 0) + (nextProjection.total.amount ?? 0)) > 0 && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link p-0 border-0 text-start"
                                                                            style={{
                                                                                lineHeight: 1.2,
                                                                                textDecoration: "none",
                                                                                color: "var(--color-mid)",
                                                                                fontSize: "inherit",
                                                                                fontWeight: 600,
                                                                                whiteSpace: "nowrap",
                                                                            }}
                                                                            title="Cost Breakdown"
                                                                            onClick={() => setShowUpcomingCostDetails(true)}
                                                                        >
                                                                            Cost Breakdown
                                                                        </button>
                                                                        <span
                                                                            className="d-none d-sm-inline"
                                                                            style={{ color: "var(--color-mid)" }}
                                                                            aria-hidden="true"
                                                                        >
                                                                            ·
                                                                        </span>
                                                                    </>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0 border-0 text-start"
                                                                    style={{
                                                                        lineHeight: 1.2,
                                                                        textDecoration: "none",
                                                                        color: "var(--color-mid)",
                                                                        fontSize: "inherit",
                                                                        fontWeight: 600,
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                    title="How billing works"
                                                                    onClick={() => setShowBillingInfo(true)}
                                                                >
                                                                    How billing works
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <div className="fw-semibold">
                                                    {isUsageBasedBilling
                                                        ? (sub.paymentMethod ? "Payment method on file" : "No payment method")
                                                        : (sub.paymentMethod
                                                            ? showDate(nextRenewalDate(furthestAccessPeriodDate) ?? furthestAccessPeriodDate)
                                                                ? `${showMoney(sub.price)} on ${showDate(nextRenewalDate(furthestAccessPeriodDate) ?? furthestAccessPeriodDate)}`
                                                                : showMoney(sub.price)
                                                            : "No payment method")}
                                                </div>
                                                <div className="text-muted small">
                                                    {isUsageBasedBilling
                                                        ? (sub.paymentMethod
                                                            ? "You will be billed by the lot based on your usage"
                                                            : "Add a payment method to enable usage-based billing")
                                                        : (sub.paymentMethod
                                                            ? "Auto-renew enabled"
                                                            : "Add a payment method to enable auto-renew")}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {!sub.isPendingPaymentMethodVerification && (
                                        <div className="d-flex flex-column align-items-end flex-shrink-0 gap-1">
                                            {sub.paymentMethod && (
                                                <div className="text-muted small d-flex align-items-center gap-1" style={{ lineHeight: 1.2 }}>
                                                    <CreditCard size={12} className="flex-shrink-0" style={{ marginTop: "-2px" }} />
                                                    <span>{sub.paymentMethod}</span>
                                                </div>
                                            )}
                                            <BsButton variant={sub.paymentMethod ? "outline-secondary" : "outline-primary"} size="sm" className="rounded-pill d-flex align-items-center gap-1"
                                                onClick={openPmModal}>
                                                <PencilFill className="small" /> {sub.paymentMethod ? "Modify" : "Add"}
                                            </BsButton>
                                        </div>
                                    )}
                                </div>
                                )}
                            </Card>

                            {planIsActive && qrLoading && (
                            <div className="mt-3 rounded-3 overflow-hidden d-flex align-items-center gap-3 px-3 py-3"
                                 style={{ backgroundColor: '#e9ecef', height: 68 }}>
                                <div className="rounded-3 flex-shrink-0 placeholder-glow"
                                     style={{ width: 40, height: 40 }}>
                                    <span className="placeholder w-100 h-100 rounded-3" />
                                </div>
                                <div className="flex-grow-1 placeholder-glow">
                                    <span className="placeholder col-5 mb-1" />
                                    <span className="placeholder col-8" style={{ height: 10 }} />
                                </div>
                            </div>
                            )}
                            {!qrLoading && qrCodeData && (
                            <div
                                className="mt-3 rounded-3 overflow-hidden d-flex align-items-center gap-3 px-3 py-3 header-gradient fade-in"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    if (sub.start.isAfter(LocalDate.now())) {
                                        toast.info(`Your subscription hasn't started yet. Your pass will be available on ${showDate(sub.start)}.`);
                                        return;
                                    }
                                    setShowQrModal(true);
                                }}
                            >
                                <div className="rounded-3 flex-shrink-0 d-flex align-items-center justify-content-center"
                                     style={{ width: 40, height: 40, backgroundColor: 'rgba(255,255,255,0.2)' }}>
                                    <QrCodeScan className="text-white" />
                                </div>
                                <div className="flex-grow-1">
                                    <div className="fw-bold text-white">Access Pass</div>
                                    <div className="small" style={{ color: 'rgba(255,255,255,0.8)' }}>Tap to show your gate QR code</div>
                                </div>
                                <ChevronRight className="text-white" />
                            </div>
                            )}
                        </Col>
                    </Row>
                    <QrCodeModal
                        show={showQrModal}
                        onHide={() => setShowQrModal(false)}
                        value={qrCodeData}
                        height={256}
                    />
                    <Row className="g-2">
                        <Col xs="12" lg="12">
                        {sub.plan.isOneTermParking &&
                            <Card>
                                <Card.Header style={{ display: "grid", gridTemplateColumns: "max-content 1fr max-content max-content" }}>
                                    <div>
                                        Parking
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    <div>
                                        {spacesDisplay.kind === 'upcoming' ? (
                                            <div>You have purchased <strong>{spaces}</strong> {spaces === 1 ? 'spot' : 'spots'} starting <strong>{showDate(spacesDisplay.startDate)}</strong>.</div>
                                        ) : (
                                            <div>You have purchased <strong>{spaces}</strong> spots.</div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        }
                            {servicesLoading && (
                            <div className="mt-3 rounded-3 placeholder-glow d-flex flex-column gap-3 p-3" style={{ backgroundColor: '#e9ecef' }}>
                                <div className="d-flex justify-content-between align-items-center gap-3">
                                    <div className="flex-grow-1 d-flex flex-column gap-2">
                                        <span className="placeholder d-block rounded" style={{ width: '40%', height: 14 }} />
                                        <span className="placeholder d-block rounded" style={{ width: '65%', height: 10 }} />
                                    </div>
                                    <span className="placeholder rounded-pill flex-shrink-0" style={{ width: 40, height: 20 }} />
                                </div>
                                <div className="d-flex justify-content-between align-items-center gap-3">
                                    <div className="flex-grow-1 d-flex flex-column gap-2">
                                        <span className="placeholder d-block rounded" style={{ width: '50%', height: 14 }} />
                                        <span className="placeholder d-block rounded" style={{ width: '45%', height: 10 }} />
                                    </div>
                                    <span className="placeholder rounded-pill flex-shrink-0" style={{ width: 40, height: 20 }} />
                                </div>
                            </div>
                            )}
                            {!servicesLoading && availableServices.length > 0 && (
                            <Card className="mt-3 fade-in">
                                <Card.Header style={{ display: "grid", gridTemplateColumns: "max-content 1fr max-content" }}>
                                    <div>
                                        Additional Services
                                    </div>
                                    <div></div>
                                    <div></div>
                                </Card.Header>
                                <Card.Body>
                                    <div className="d-flex flex-column gap-3">
                                        {availableServices.map((fee, index) => (
                                                <div key={fee.operatorFeeId || index}>
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div className="d-flex flex-column">
                                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                                <Badge className={serviceSelection[(fee.operatorFeeId || 0)] ? "" : "bg-secondary"}>
                                                                    {(fee as any)?.status || (serviceSelection[(fee.operatorFeeId || 0)] ? "Active" : "Not Active")}
                                                                </Badge>
                                                                <div className="fw-bold">{fee.name || "Service"}</div>
                                                            </div>
                                                            <div className="text-muted small">
                                                                <b>Price:</b> {formatServiceAmount(fee)}
                                                                {(fee as any).isPerSpace && <span className="ms-1">/ space</span>}
                                                            </div>
                                                            {(fee as any)?.receiptNum && (
                                                                <div className="text-muted small mt-1">
                                                                    <b>Confirmation #: </b>{(fee as any).receiptNum}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="form-check form-switch">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                role="switch"
                                                                id={`service-${fee.operatorFeeId || index}`}
                                                                checked={!!serviceSelection[(fee.operatorFeeId || 0)]}
                                                                disabled={!planIsActive}
                                                                onChange={(e) => handleServiceToggle(fee.operatorFeeId || 0, e.target.checked)}
                                                            />
                                                        </div>
                                                    </div>
                                                    {(fee as any)?.receiptDescription && (
                                                        <>
                                                            <hr className="my-2" />
                                                            <div style={{ marginBottom: "-15px" }}
                                                                className="text-muted small mt-1 "
                                                                dangerouslySetInnerHTML={{ __html: (fee as any).receiptDescription }}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                    </div>
                                </Card.Body>
                            </Card>
                            )}

                            {!isUsageBasedBilling && !isPending && (
                            <Card className="mt-3">
                                <Card.Header
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "max-content 1fr max-content",
                                    alignItems: "center",
                                }}
                                >
                                <div>
                                    Parkers
                                </div>

                                <div />

                                <div>
                                    {(planIsActive && !isCalculatedGroup) && (
                                    <Link
                                    to=""
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowInviteParker(true);
                                    }}
                                    >
                                    Add Parker
                                    </Link>
                                    )}
                                </div>
                                </Card.Header>

                                <Card.Body className="p-0 m-0 border-0">
                                        {(isCalculatedGroup) ? (
                                            <div className="p-3">
                                                <GroupParkerListSection
                                                    sub={sub}
                                                    parkers={parkers}
                                                    setParkers={setParkers}
                                                    paymentMethods={groupPaymentMethods}
                                                    sectionRef={groupParkerSectionRef}
                                                    planIsActive={planIsActive}
                                                    isEnhancedGroup={isEnhancedGroup}
                                                    groups={groups}
                                                    onGroupsChange={setGroups}
                                                    onManageVehicles={(sp) => {
                                                        setSelectedParker(sp);
                                                        setShowGroupParkerVehiclesModal(true);
                                                    }}
                                                    onRemoveParker={(sp) => {
                                                        setRemoveParkerEntry(sp);
                                                        setShowRemove(true);
                                                    }}
                                                    onUndoCancelParker={(sp) => {
                                                        void parkerUndoCancel(sp.parker.parkerId);
                                                    }}
                                                    onRemoveBaseSlot={() => setShowRemoveSlot(true)}
                                                    onRefreshParkers={async () => {
                                                        const result = await Api.subscriptionDetails(planId);
                                                        if (!result.isOk) {
                                                            toast.error("Could not refresh parkers.");
                                                            return parkers;
                                                        }
                                                        const refreshed = parseSubscription(result.value);
                                                        setSub(refreshed);
                                                        return refreshed.parkers.filter(
                                                            (p) => !isEffectiveCancellation(p.removedWhen, sub.facility.timeZone),
                                                        );
                                                    }}
                                                />
                                            </div>
                                        ) : parkers.length === 0 ? (
                                            <div className="grid-col-1 text-secondary p-3">
                                            Invite others to this plan to let them manage their own vehicles.
                                            </div>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table mb-0 align-middle">
                                                    <thead className="table-light">
                                                        <tr className="small text-muted">
                                                            <th className="py-2 fw-normal">Name</th>
                                                            <th className="py-2 fw-normal">Cancel Date</th>
                                                            <th className="text-end py-2 fw-normal"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {parkers.map((p) => {
                                                            const parker = p.parker;

                                                            return (
                                                                <tr key={parker.parkerId}>
                                                                    <td className="py-3">
                                                                        {sub.status === SubscriptionStatus.Active ? (
                                                                            <Hyperlink
                                                                                onClick={() => {
                                                                                    setSelectedParker(p);
                                                                                    setShowLinkVehicleModal(true);
                                                                                }}
                                                                            >
                                                                                {parker.name}
                                                                            </Hyperlink>
                                                                        ) : (
                                                                            <span>{parker.name}</span>
                                                                        )}
                                                                        {parker.isManaged && (
                                                                            <div className="small text-secondary">
                                                                                {parker.emailAddress}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td className="py-3">
                                                                        <span className={p.removedWhen ? "text-danger small" : "text-muted small"}>
                                                                            {formatParkerCancelDate(p.removedWhen, facility.timeZone)}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-end py-3 pe-3">
                                                                        {planIsActive && (
                                                                            <CloseButton
                                                                                onClick={() => {
                                                                                    setRemoveParkerEntry(p);
                                                                                    setShowRemove(true);
                                                                                }}
                                                                                className="cursor-pointer"
                                                                            />
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                </Card.Body>
                            </Card>
                            )}
                        </Col>
                    </Row>
                </Container>
            {/* Confirmation modal for service changes */}
            <Modal centered show={showServiceConfirm} onEscapeKeyDown={() => setShowServiceConfirm(false)}>
                <Modal.Header>
                    <HeaderText>Confirm Service Change</HeaderText>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-2">
                        {pendingServiceNewValue ? (
                            <>
                                <div className="mb-2">Are you sure you want to activate <strong>{pendingService?.name}</strong>?</div>
                                {/* Currently not possible to reactivate for ParkGuard */}
                                {(pendingService as any)?.status === "Confirmed" ? (
                                    <div>The service will be activated for <strong>{pendingService ? formatServiceAmount(pendingService) : ''}</strong> with the next renewal cycle on {furthestAccessPeriodDate ? <strong>{showDate(furthestAccessPeriodDate)}</strong> : 'the end of the current period'} and will be renewed thereafter.</div>
                                ) : (
                                    <div>The service will be activated with the next renewal cycle on {furthestAccessPeriodDate ? <strong>{showDate(furthestAccessPeriodDate)}</strong> : 'the end of the current period'} and will be renewed thereafter.</div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="mb-2">Are you sure you want to cancel <strong>{pendingService?.name}</strong>?</div>
                                {(pendingService as any)?.status === "Confirmed" && (
                                    <div>
                                        The service will be discontinued on {furthestAccessPeriodDate ? <strong>{showDate(furthestAccessPeriodDate)}</strong> : 'the end of the current period'} and you will not be charged on the next renewal cycle.
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button className="m-2" onClick={cancelServiceChange}>Cancel</Button>
                    <Button className="btn-main m-2" onClick={confirmServiceChange}>{pendingServiceNewValue ? "Confirm" : "Confirm Cancellation"}</Button>
                </Modal.Footer>
            </Modal>
              
              {!isCalculatedGroup && !isUsageBasedBilling && !isPending && (
                <Card className="mt-3">
                    <Card.Header style={{ display: "grid", gridTemplateColumns: "max-content 1fr max-content max-content" }}>
                        <div>
                            Vehicles
                        </div>
                        <div>

                        </div>
                        <div>
                            {canAddVehicle && (
                                <>
                                    <Link to="" onClick={() => setShowAdd(true)}>Add New</Link>
                                    {myVehicles.length !== 0 && <>&nbsp;|&nbsp;<Link to="" onClick={() => openChooseModalForSubList()}>Add Existing</Link></>}
                                </>
                            )}
                        </div>
                    </Card.Header>
                    <Card.Body>
                        {planIsActive && !canAddVehicle && (
                            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-2 small" role="alert">
                                <InfoCircleFill className="flex-shrink-0 mt-1" />
                                <span>No more vehicles can be added to the subscription.</span>
                            </div>
                        )}
                        {subVehicles.length === 0 ? (
                            <div className="d-flex flex-column align-items-center justify-content-center text-center py-3">
                                <div
                                    className="rounded-circle mb-3"
                                    style={{
                                        width: 72,
                                        height: 72,
                                        backgroundColor: "rgba(220, 53, 69, 0.15)",
                                    }}
                                />
                                <p className="text-secondary mb-4 mx-auto" style={{ maxWidth: 360 }}>
                                    Add vehicles to authorize them to access the lot as part of this subscription.
                                </p>
                                {canAddVehicle && (
                                    <Button className="btn-main px-4" onClick={() => setShowAdd(true)}>
                                        Add Vehicle
                                    </Button>
                                )}
                            </div>
                        ) : (
                            subVehicles.map(v => (
                                <ReservationVehicleCard
                                    key={v.vehicleId}
                                    vehicle={v}
                                    onRemove={vehicleRemove}
                                    subscriptionStatus={sub.status}
                                />
                            ))
                        )}
                    </Card.Body>
                </Card>
              )}
                {!isCalculatedGroup && !isComplimentaryPlan && !isPending && (
                <Card className="mt-3">
                    <Card.Header>
                        <div>
                            Access Periods
                        </div>
                    </Card.Header>
                    <Card.Body className="position-relative p-0">
                        <div style={{ maxHeight: 300, overflowY: 'auto', padding: '0 1rem 1rem' }}>
                            <div style={{ display: "grid", gridTemplateColumns: "auto auto auto" }}>
                                <div className="border-bottom border-primary border-1 text-center position-sticky bg-white fw-semibold small text-muted" style={{ top: 0, zIndex: 1, paddingTop: '1rem', paddingBottom: '0.25rem' }}>Spots</div>
                                <div className="border-bottom border-primary border-1 text-center position-sticky bg-white fw-semibold small text-muted" style={{ top: 0, zIndex: 1, paddingTop: '1rem', paddingBottom: '0.25rem' }}>Start</div>
                                <div className="border-bottom border-primary border-1 text-center position-sticky bg-white fw-semibold small text-muted" style={{ top: 0, zIndex: 1, paddingTop: '1rem', paddingBottom: '0.25rem' }}>End</div>
                                {[...sub.accessPeriods].sort((a, b) => b.accessPeriodId - a.accessPeriodId).map(ap => (
                                    <React.Fragment key={ap.accessPeriodId}>
                                        <div className="text-center py-1">{ap.quantity} Spots</div>
                                        <div className="text-center py-1">{showDate(ap.start)}</div>
                                        <div className="text-center py-1">{showDate(ap.end)}</div>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                        {sub.accessPeriods.length > 6 && (
                        <div className="position-absolute bottom-0 start-0 end-0 pe-none"
                             style={{ height: 40, background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))' }} />
                        )}
                    </Card.Body>
                </Card>
                )}
                 <Button type="button" className="w-50 my-4" onClick={() => nav(backTo)}>
                    Back
                </Button>
            </Col>
        </Row>
            <WaitlistNoticeModal
                show={showWaitlistModal}
                requestPayload={{
                    parkerId: loadedSub.parkerId,
                    facilityId: facility.facilityId,
                    planEntryId: loadedSub.planEntryId,
                    spacesRequested: loadedSub.quantity,
                    isAddingToExistingParker: true,
                    subscriptionId: loadedSub.subscriptionId
                }}
                onCancel={() => setShowWaitlistModal(false)}
                onWaitlistSuccess={async () => await changeSubscriptionStatus(SubscriptionStatus.Waitlisted)}
            />

            <NoMoreSpacesModal
                show={showNoSpacesModal}
                setShow={setShowNoSpacesModal}
                planId={planId}
            />

            <GroupModal
            show={showGroupModal}
            onClose={() => setShowGroupModal(false)}
            group={editingGroup}
            existingGroups={groups}
            onSubmit={async (name, groupId) => {
                if (groupId) {
                await updateGroup(groupId, name);
                } else {
                await createGroup(name);
                }
            }}
            />

            <ContractDetailsModal
            show={showContractModal}
            contract={selectedContract}
            onHide={() => {
                setShowContractModal(false);
                setSelectedContract([]);
                setSelectedGroupName(undefined);
            }}
            groupName={selectedGroupName!}
            basePriceCents={resolvePlanBasePriceCents(
                sub.prices,
                sub.planPriceId,
                sub.quantity > 0
                    ? Math.round(toCents(sub.price) / sub.quantity)
                    : toCents(sub.price),
            )}
            />

            <ConfirmModal
            title="Remove Group"
            message={
            groupToRemove ? (
                <>
                Are you sure you want to remove this group?{" "}
                {groupToRemove.parkerCount! > 0 && (
                <strong>
                    All {groupToRemove.parkerCount} parkers will be removed from the group.
                </strong>
                    )}
                </>
            
            ) : null
            }
            show={showRemoveGroup}
            setShow={setShowRemoveGroup}
            onConfirm={() => {
                if (groupToRemove?.id) {
                deleteGroup(groupToRemove.id);
                }
            }}
            />

            <Modal centered show={showPendingCancellationModifyModal} onHide={() => setShowPendingCancellationModifyModal(false)}>
                <Modal.Header closeButton>
                    <HeaderText>Cannot Modify Plan</HeaderText>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex align-items-start gap-3 text-secondary">
                        <ExclamationTriangleFill className="text-warning flex-shrink-0 mt-1" size={24} />
                        <span>
                            This subscription is pending cancellation and cannot be modified.
                            {sub.end && (
                                <> Access ends {showDate(sub.end)}.</>
                            )}
                            {' '}Undo cancellation above to make changes.
                        </span>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button className="w-100" onClick={() => setShowPendingCancellationModifyModal(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
    </Container>;
}